import { EffectSelectionOptions, } from '../effect-selection-constants.js';
import { FeedbackReasonOptions, PraiseReasonOptions, SharedReasonOptions, } from '../reason-selection-constants.js';
export const wizardEnTranslations = {
    // Reason Selection Constants (for direct constant value lookup)
    reasonConstants: {
        'collaboration-service': 'Collaboration / Service',
        'schedule-agreement': 'Schedule / Agreement',
        communication: 'Communication',
        'information-sharing': 'Information Sharing',
        'praise-feedback': 'Praise & Feedback',
        'meeting-expectations': 'Meeting Expectations',
        'expectations-setting': 'Expectations Setting',
    },
    questions: {
        firstQuestion: {
            title: ({ sender }) => `${sender?.personalInfo?.firstName || ''} Hello there! With whom would you like to initiate a conversation?`,
            placeholder: 'Search people in the company...',
            validation: 'Please select a person',
        },
        dateSelection: {
            title: ({ recipient }) => `Can you tell me the date of the event involving ${recipient?.personalInfo?.firstName || 'this person'}?`,
            validation: 'Please select a date',
        },
        collaborationServiceType: {
            title: 'Please select a subject',
            description: 'What type of collaboration or service interaction?',
            validation: 'Please select a type',
            options: {
                serviceResponse: {
                    title: 'Response/Service Received',
                    description: 'Service or response received',
                },
                collaboration: {
                    title: 'Collaboration',
                    description: 'Working together on projects or tasks',
                },
            },
        },
        scheduleAgreementType: {
            title: 'Please select a subject',
            description: 'What type of schedule or agreement interaction?',
            validation: 'Please select a type',
            options: {
                procedure: {
                    title: 'Working According to Procedure',
                    description: 'Following established procedures and protocols',
                },
                agreementPromise: {
                    title: 'Keeping Agreements or Promises',
                    description: 'Following through on commitments made',
                },
                schedule: {
                    title: 'Keeping to Schedule',
                    description: 'Meeting deadlines and time commitments',
                },
                goalMeeting: {
                    title: 'Meeting a Goal',
                    description: 'Achieving set objectives',
                },
            },
        },
        // Shared Praise/Feedback Recipient Selection
        praiseFeedbackRecipient: {
            validation: 'Please select who should have given the feedback',
            options: {
                employees: {
                    title: 'One or More Employees',
                    description: 'Other team members should have provided the feedback',
                },
                me: {
                    title: 'Me',
                    description: 'I should have provided the feedback',
                },
            },
        },
        // Shared Praise/Feedback Employee Selection
        praiseFeedbackEmployeeSelection: {
            title: 'Who?',
            placeholder: 'Select employees who should have provided feedback',
            validation: 'Please select at least one employee',
        },
        bodyLanguageFrameworkSelection: {
            title: 'In which framework did the discussion take place?',
            validation: 'Please select a framework',
        },
        writtenCommunicationFrameworkSelection: {
            title: 'In which framework were they written?',
            validation: 'Please select a communication framework',
        },
        verbalExpressionsFrameworkSelection: {
            title: 'In which framework were they said?',
            validation: 'Please select a communication framework',
        },
        frameworkSelection: {
            validation: 'Please select a framework',
            options: {
                oneOnOne: { title: 'One-on-one' },
                meeting: { title: 'Meeting' },
                casualConversation: { title: 'Casual conversation' },
                phoneCall: { title: 'Phone call' },
                email: { title: 'Email' },
                instantMessage: { title: 'Instant message (e.g. WhatsApp)' },
                socialMedia: { title: 'Social media' },
                solvePlatform: { title: 'INTERA platform' },
                other: { title: 'Another way' },
                notRelevant: { title: 'Not relevant' },
            },
        },
        companyValues: {
            title: ({ recipient, answers, }) => {
                const reasonToTitle = {
                    communication: `Does ${recipient?.personalInfo?.firstName || 'this person'}'s communication style during the discussion contribute to the reinforcement of company values?`,
                    'collaboration-service': `Does ${recipient?.personalInfo?.firstName || 'this person'}'s response / service contribute to the reinforcement of company values?`,
                    'information-sharing': `Does ${recipient?.personalInfo?.firstName || 'this person'}'s shared information contribute to the reinforcement of company values?`,
                    'schedule-agreement': `Does ${recipient?.personalInfo?.firstName || 'this person'} keeping to the schedule / keeping to an agreement / following procedures / meeting a goal contribute to the reinforcement of company values?`,
                    'praise-feedback': `Do ${recipient?.personalInfo?.firstName || 'this person'}'s praise and feedback contribute to the reinforcement of company values?`,
                    'meeting-expectations': `Does ${recipient?.personalInfo?.firstName || 'this person'} meeting expectations contribute to the reinforcement of company values?`,
                    default: `Does ${recipient?.personalInfo?.firstName || 'this person'} contribute to the reinforcement of company values?`,
                };
                return (reasonToTitle[answers?.['reason-selection']] || reasonToTitle.default);
            },
            validation: 'Please select at least one value',
        },
        goals: {
            productivity: {
                title: 'Boost Productivity',
                description: 'Increase efficiency and output quality',
            },
            teamwork: {
                title: 'Improve Teamwork',
                description: 'Enhance collaboration and team dynamics',
            },
            innovation: {
                title: 'Drive Innovation',
                description: 'Foster creative thinking and new ideas',
            },
            customerSatisfaction: {
                title: 'Customer Satisfaction',
                description: 'Improve customer experience and satisfaction',
            },
            quality: {
                title: 'Maintain Quality',
                description: 'Ensure high standards and quality delivery',
            },
        },
        values: {
            integrity: {
                title: 'Integrity',
                description: 'Acting with honesty and strong moral principles',
            },
            collaboration: {
                title: 'Collaboration',
                description: 'Working together effectively as a team',
            },
            excellence: {
                title: 'Excellence',
                description: 'Striving for the highest quality in everything we do',
            },
            innovation: {
                title: 'Innovation',
                description: 'Embracing creativity and new ways of thinking',
            },
        },
    },
    // Praise Questions Wizard (unique praise-specific content)
    praiseQuestions: {
        title: 'Praise & Feedback Wizard',
        description: 'Share your positive workplace experiences',
        praiseSubmittedSuccessfully: 'Praise submitted successfully',
        praiseSubmittedFailed: 'Failed to submit praise',
        // Praise/Feedback Recipient Selection for Praise Wizard
        praiseFeedbackRecipient: {
            title: ({ recipient, answers, }) => {
                const recipientName = recipient?.personalInfo?.firstName || 'this person';
                const praiseTypeSelection = answers?.['praise-type-selection']?.[0];
                switch (praiseTypeSelection) {
                    case 'praise-only':
                        return `Who did ${recipientName} give I See U to?`;
                    case 'feedback-only':
                        return `Who did ${recipientName} give I Need U to?`;
                    case 'praise-and-feedback':
                        return `Who did ${recipientName} give I See U and I Need U to?`;
                    default:
                        return `Who did ${recipientName} give I See U and I Need U to?`;
                }
            },
        },
        // Praise/Feedback Employee Selection for Praise Wizard
        praiseFeedbackEmployeeSelection: {
            title: 'Who?',
            placeholder: 'Select employees who received praise or feedback',
            validation: 'Please select at least one employee',
        },
        goalSelection: {
            title: ({ recipient, answers, }) => {
                const reasonToTitle = {
                    communication: `Which one of your goals is affected by ${recipient?.personalInfo?.firstName || 'this person'}'s communication style during the discussion?`,
                    'meeting-expectations': `Which one of your goals is affected by ${recipient?.personalInfo?.firstName || 'this person'} meeting expectations?`,
                    default: `Which one of your goals is affected by ${recipient?.personalInfo?.firstName || 'this person'}?`,
                };
                const scheduleAgreementTypeToTitle = {
                    procedure: `which one of your goals is affected by ${recipient?.personalInfo?.firstName || 'this person'} working according to procedure?`,
                    'agreement-promise': `which one of your goals is affected by ${recipient?.personalInfo?.firstName || 'this person'} keeping to what was promised or agreed upon?`,
                    schedule: `which one of your goals is affected by ${recipient?.personalInfo?.firstName || 'this person'} keeping to the schedule?`,
                    'goal-meeting': `The goal met by ${recipient?.personalInfo?.firstName || 'this person'}, affected?`,
                };
                const collaborationServiceTypeToTitle = {
                    'service-response': `which one of your goals is affected by ${recipient?.personalInfo?.firstName || 'this person'}'s response/service?`,
                    collaboration: `which one of your goals is affected by collaboration with ${recipient?.personalInfo?.firstName || 'this person'}?`,
                };
                const informationSharingTypeToTitle = {
                    'general-info': `which one of your goals is affected by ${recipient?.personalInfo?.firstName || 'this person'}'s shared information?`,
                    reporting: `which one of your goals is affected by ${recipient?.personalInfo?.firstName || 'this person'}'s reporting?`,
                    'decision-making': `which one of your goals is affected by ${recipient?.personalInfo?.firstName || 'this person'} involving you in the decision-making process?`,
                };
                const praiseFeedbackTypeToTitle = {
                    'praise-only': `which one of your goals does ${recipient?.personalInfo?.firstName || 'this person'}'s I See U affect?`,
                    'feedback-only': `which one of your goals does ${recipient?.personalInfo?.firstName || 'this person'}'s I Need U affect?`,
                    'praise-and-feedback': `which one of your goals does ${recipient?.personalInfo?.firstName || 'this person'}'s I See U and I Need U affect?`,
                };
                if (answers?.['reason-selection']?.[0] === 'collaboration-service') {
                    return collaborationServiceTypeToTitle[answers?.['collaboration-service-type']?.[0]];
                }
                if (answers?.['reason-selection']?.[0] === 'schedule-agreement') {
                    return scheduleAgreementTypeToTitle[answers?.['schedule-agreement-type']?.[0]];
                }
                if (answers?.['reason-selection']?.[0] === 'information-sharing') {
                    return informationSharingTypeToTitle[answers?.['information-sharing-type']?.[0]];
                }
                if (answers?.['reason-selection']?.[0] === 'praise-feedback') {
                    return praiseFeedbackTypeToTitle[answers?.['praise-type-selection']?.[0]];
                }
                return (reasonToTitle[answers?.['reason-selection']?.[0]] || reasonToTitle.default);
            },
            validation: 'Please select a goal',
        },
        reasonSelection: {
            title: ({ recipient }) => `What does the event with ${recipient?.personalInfo?.firstName || 'this person'} is about?`,
            validation: 'Please select a reason',
            options: {
                collaboration: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'}'s collaboration or response / service`,
                },
                scheduleAgreement: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'} keeping to the schedule / keeping to an agreement / following procedures / meeting a goal`,
                },
                communication: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'}'s communication style (the manner of speech, writing or behavior)`,
                },
                informationSharing: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'} sharing information / reporting / involving me in the decision-making process`,
                },
                praiseFeedback: {
                    title: ({ recipient }) => `I See U and / or I Need U given by ${recipient?.personalInfo?.firstName || 'this person'}`,
                },
                meetingExpectations: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'}'s meeting expectations`,
                },
            },
        },
        expectationsDetails: {
            title: ({ recipient }) => `In which area did ${recipient?.personalInfo?.firstName || 'this person'} met expectations?`,
            placeholder: 'Write briefly...',
            validation: 'Please provide details',
        },
        praiseTypeSelection: {
            title: 'What type of event is it?',
            validation: 'Please select an interaction type',
            options: {
                praiseAndFeedback: {
                    title: ({ recipient }) => `I See U and I Need U given by ${recipient?.personalInfo?.firstName || 'this person'}`,
                },
                feedbackOnly: {
                    title: ({ recipient }) => `I Need U given by ${recipient?.personalInfo?.firstName || 'this person'}`,
                },
                praiseOnly: {
                    title: ({ recipient }) => `I See U given by ${recipient?.personalInfo?.firstName || 'this person'}`,
                },
            },
        },
        collaborationServiceType: {
            title: 'Please select a subject?',
            validation: 'Please select a type',
            options: {
                serviceResponse: {
                    title: ({ recipient }) => `Response / service I received from ${recipient?.personalInfo?.firstName || 'this person'}`,
                    description: 'Response to a service request',
                },
                collaboration: {
                    title: ({ recipient }) => `Collaboration with ${recipient?.personalInfo?.firstName || 'this person'}`,
                    description: 'Working together on a project or task',
                },
            },
        },
        frameworkSelection: {
            title: ({ answers, recipient, }) => {
                // Check which flow we're in based on previous steps
                if (answers?.['collaboration-service-type']) {
                    if (answers?.['collaboration-service-type']?.[0] === 'collaboration') {
                        return `In which framework did you collaborate with ${recipient?.personalInfo?.firstName || 'this person'}?`;
                    }
                    if (answers?.['collaboration-service-type']?.[0] === 'service-response') {
                        return `In which framework did ${recipient?.personalInfo?.firstName || 'this person'} respond/provided a service?`;
                    }
                }
                if (answers?.['communication-type-selection']) {
                    return 'praise-How should the communication have been handled?';
                }
                // Check if this is from the praise wizard expectations flow
                if (answers?.['reason-selection']?.[0] === 'meeting-expectations') {
                    return `In which framework did ${recipient?.personalInfo?.firstName || 'this person'} met expectations?`;
                }
                if (answers?.['reason-selection']?.[0] === 'praise-feedback') {
                    if (answers?.['praise-type-selection']?.[0] === 'praise-and-feedback') {
                        return 'In what framework was the I See U and I Need U given?';
                    }
                    if (answers?.['praise-type-selection']?.[0] === 'feedback-only') {
                        return 'In which framework was the I Need U given?';
                    }
                    if (answers?.['praise-type-selection']?.[0] === 'praise-only') {
                        return 'In which framework was the I See U given?';
                    }
                }
                if (answers?.['reason-selection']?.[0] === 'information-sharing') {
                    if (answers['information-sharing-type']?.[0] === 'general-info') {
                        return 'In which framework did the information sharing take place?';
                    }
                    if (answers['information-sharing-type']?.[0] === 'reporting') {
                        return 'In which framework was the report given?';
                    }
                    if (answers['information-sharing-type']?.[0] === 'decision-making') {
                        return 'Which framework was used when involving you in the decision-making process?';
                    }
                }
                // Fallback to generic
                return 'How should this have been handled?';
            },
        },
        communicationType: {
            title: 'What aspect of communication?',
            validation: 'Please select a communication type',
            options: {
                speechTone: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'their'}'s style of speech and tone`,
                    description: 'The tone and manner of speaking',
                },
                generalConduct: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'their'}'s general conduct during the discussion`,
                    description: 'Their behavior during conversations',
                },
                bodyLanguage: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'their'}'s gestures`,
                    description: 'body lanugage, facial expression etc.',
                },
                writtenMessages: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'their'}'s Written Messages`,
                    description: 'Things written in emails, messages, etc.',
                },
                verbalExpressions: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'their'}'s Verbal Expressions`,
                    description: 'Things said verbally',
                },
            },
        },
        scheduleAgreementType: {
            title: 'What type of schedule or agreement?',
            validation: 'Please select a type',
            options: {
                procedure: {
                    title: 'Working according to procedure',
                    description: 'Following established procedures',
                },
                agreementPromise: {
                    title: 'Keeping to an agreement or promise',
                    description: 'Keeping agreements and promises',
                },
                schedule: {
                    title: 'Keeping to the schedule',
                    description: 'Managing time and schedules effectively',
                },
                goalMeeting: {
                    title: 'Meeting a goal',
                    description: 'Meeting goals and objectives',
                },
            },
        },
        goalImpactRange: {
            title: 'To what extent are the chances of achieving your goal improved?',
            minLabel: 'Low Impact',
            maxLabel: 'High Impact',
            validation: 'Please rate the impact',
        },
        additionalEffectsTemp: {
            title: 'in which other ways does this event affect you?',
            validation: 'Please select at least one effect',
            options: {
                [EffectSelectionOptions.ENERGY]: {
                    title: 'Energizes me',
                    beforeSend: (name, impacted) => `${name} makes you feel more energized in relation to ${impacted}.`,
                    afterSend: (name, impacted) => `You make ${name} feel more energized in relation to ${impacted}.`,
                },
                [EffectSelectionOptions.CONFIDENCE]: {
                    title: 'Makes me feel more confident',
                    beforeSend: (name, impacted) => `${name} makes you feel more confident in relation to ${impacted}.`,
                    afterSend: (name, impacted) => `You increase ${name}'s confidence in relation to ${impacted}.`,
                },
                [EffectSelectionOptions.CONNECTION]: {
                    title: 'Makes us feel more connected',
                    beforeSend: (name) => `You feel more connected to ${name}.`,
                    afterSend: (name) => `You make ${name} feel more connected to you.`,
                },
                [EffectSelectionOptions.REPUTATION]: {
                    title: 'Raises my professional reputation',
                    beforeSend: (name, impacted) => `${name} raises your professional reputation in the eyes of ${impacted}.`,
                    afterSend: (name, impacted) => `You raise ${name}'s professional reputation in the eyes of ${impacted}.`,
                },
                [EffectSelectionOptions.MEANINGFUL]: {
                    title: 'Makes me feel more meaningful',
                    beforeSend: (name) => `${name} makes you feel more meaningful.`,
                    afterSend: (name) => `You make ${name} feel more meaningful.`,
                },
                [EffectSelectionOptions.BELONGING]: {
                    title: 'Increases my sense of belonging',
                    beforeSend: (name, impacted) => `${name} increases your sense of belonging in relation to ${impacted}.`,
                    afterSend: (name, impacted) => `You increase ${name}'s sense of belonging in relation to ${impacted}.`,
                },
                [EffectSelectionOptions.RESPONSIBILITY]: {
                    title: 'Empowers me to initiate and take more responsibility',
                    beforeSend: (name, impacted) => `${name} empowers you to initiate and take on more responsibility in relation to ${impacted}.`,
                    afterSend: (name, impacted) => `You empower ${name} to initiate and take on more responsibility in relation to ${impacted}.`,
                },
                [EffectSelectionOptions.LEARNING]: {
                    title: 'Increases my ability to learn and develop myself',
                    beforeSend: (name) => `${name} increases your ability to learn and develop yourself.`,
                    afterSend: (name) => `You increase ${name}'s ability to learn and develop.`,
                },
                [EffectSelectionOptions.NO_OTHER_EFFECTS]: {
                    title: 'Does not affect me in other ways',
                },
            },
        },
        additionalEffects: {
            title: 'in which other ways does this event affect you?',
            validation: 'Please select at least one effect',
            options: {
                [EffectSelectionOptions.ENERGY]: {
                    title: 'Energizes me',
                    beforeSend: (name) => `${name} makes you feel more energized.`,
                    afterSend: (name) => `You make ${name} feel more energized.`,
                },
                [EffectSelectionOptions.CONFIDENCE]: {
                    title: 'Makes me feel more confident',
                    beforeSend: (name) => `${name} makes you feel more confident.`,
                    afterSend: (name) => `You increase ${name}'s confidence.`,
                },
                [EffectSelectionOptions.CONNECTION]: {
                    title: 'Makes us feel more connected',
                    beforeSend: (name) => `You feel more connected to ${name}.`,
                    afterSend: (name) => `You make ${name} feel more connected to you.`,
                },
                [EffectSelectionOptions.REPUTATION]: {
                    title: 'Raises my professional reputation',
                    beforeSend: (name) => `${name} raises your professional reputation.`,
                    afterSend: (name) => `You raise ${name}'s professional reputation.`,
                },
                [EffectSelectionOptions.MEANINGFUL]: {
                    title: 'Makes me feel more meaningful',
                    beforeSend: (name) => `${name} makes you feel more meaningful.`,
                    afterSend: (name) => `You make ${name} feel more meaningful.`,
                },
                [EffectSelectionOptions.BELONGING]: {
                    title: 'Increases my sense of belonging',
                    beforeSend: (name) => `${name} increases your sense of belonging.`,
                    afterSend: (name) => `You increase ${name}'s sense of belonging.`,
                },
                [EffectSelectionOptions.RESPONSIBILITY]: {
                    title: 'Empowers me to initiate and take more responsibility',
                    beforeSend: (name) => `${name} empowers you to initiate and take on more responsibility.`,
                    afterSend: (name) => `You empower ${name} to initiate and take on more responsibility.`,
                },
                [EffectSelectionOptions.LEARNING]: {
                    title: 'Increases my ability to learn and develop myself',
                    beforeSend: (name) => `${name} increases your ability to learn and develop yourself.`,
                    afterSend: (name) => `You increase ${name}'s ability to learn and develop.`,
                },
                [EffectSelectionOptions.NO_OTHER_EFFECTS]: {
                    title: 'Does not affect me in other ways',
                },
            },
        },
        informationSharingType: {
            title: 'What sort of information sharing was it?',
            validation: 'Please select a type',
            options: {
                generalInfo: {
                    title: 'General Information Sharing',
                    description: 'Sharing general knowledge or information',
                },
                reporting: {
                    title: 'Reporting',
                    description: 'Providing reports or status updates',
                },
                decisionMaking: {
                    title: 'Invloving me in the decision-making process in the area of my responsibility',
                    description: '',
                },
            },
        },
        // Subject detail steps
        praiseFeedbackSubject: {
            title: ({ answers, recipient, }) => {
                const praiseFeedbackSubject = answers?.['praise-type-selection']?.[0];
                switch (praiseFeedbackSubject) {
                    case 'praise-and-feedback':
                        return `What was the subject of ${recipient?.personalInfo?.firstName || 'this person'}'s I See U and I Need U?`;
                    case 'feedback-only':
                        return `What was the subject of ${recipient?.personalInfo?.firstName || 'this person'}'s I Need U?`;
                    case 'praise-only':
                        return `What was the subject of ${recipient?.personalInfo?.firstName || 'this person'}'s I See U?`;
                    default:
                        return `What was the subject of ${recipient?.personalInfo?.firstName || 'this person'} I See U and I Need U?`;
                }
            },
            placeholder: 'write briefly about the subject...',
            validation: 'please describe the subject',
        },
        feedbackSubject: {
            title: 'What was the subject of their feedback?',
            description: 'Describe the topic or area that the feedback covered',
            placeholder: 'Write briefly about the subject...',
            validation: 'Please describe the subject',
        },
        praiseSubject: {
            title: 'What was the subject of their praise?',
            description: 'Describe the topic or area that the praise covered',
            placeholder: 'Write briefly about the subject...',
            validation: 'Please describe the subject',
        },
        // Framework steps
        serviceFramework: {
            title: ({ recipient }) => `In which framework did ${recipient?.personalInfo?.firstName || 'they'} respond/provided a service?`,
            description: 'Select the context where you received the service or response',
            validation: 'Please select a framework',
        },
        serviceDescription: {
            title: ({ recipient }) => `Describe ${recipient?.personalInfo?.firstName || 'their'}'s response / service`,
            placeholder: 'Write briefly about the service...',
            validation: 'Please describe the service',
        },
        collaborationFramework: {
            title: ({ recipient }) => `In which framework did you collaborate with ${recipient?.personalInfo?.firstName || 'this person'}?`,
            description: 'Select the context where the collaboration took place',
            validation: 'Please select a framework',
        },
        collaborationDescription: {
            title: 'Describe how you collaborated with each other?',
            description: 'What type of collaboration did you experience?',
            placeholder: 'Write briefly about the collaboration...',
            validation: 'Please describe the collaboration',
        },
        // Information Sharing Details Step (Dynamic)
        informationSharingDetails: {
            title: ({ answers }) => {
                const infoTypeToTitle = {
                    'general-info': 'What information was shared?',
                    reporting: 'What was the subject of the report?',
                    'decision-making': 'Describe the decision made',
                    generic: 'Please provide details',
                };
                const infoType = answers?.['information-sharing-type'];
                return infoTypeToTitle[infoType] || infoTypeToTitle.generic;
            },
            validation: 'Please provide details',
        },
        // Information Sharing Framework Step (Dynamic)
        informationSharingFramework: {
            title: ({ answers }) => {
                const infoTypeToTitle = {
                    'general-info': 'In which framework did the information sharing take place?',
                    reporting: 'In which framework was the report given?',
                    'decision-making': 'Which framework was used when involving you in the decision-making process?',
                    generic: 'In which framework did this take place?',
                };
                const infoType = answers?.['information-sharing-type'];
                return infoTypeToTitle[infoType] || infoTypeToTitle.generic;
            },
            description: 'Select the context where this took place',
            validation: 'Please select a framework',
        },
        // Communication details
        speechToneDetails: {
            title: ({ recipient }) => `Which aspect of ${recipient?.personalInfo?.firstName || 'their'}'s speech style and tone do you want to praise?`,
            description: 'Select the specific aspect of their communication style',
            validation: 'Please select an aspect',
            options: {
                enthusiastic: {
                    title: 'Spoke Enthusiastically and not monotonously',
                    description: 'Engaging and energetic way of speaking',
                },
                quickFluent: {
                    title: 'Spoke Quickly and Fluently',
                    description: 'Clear and articulate communication',
                },
                calm: {
                    title: 'Spoke Calmly',
                    description: 'Peaceful and composed manner of speaking',
                },
                appropriateLaughter: {
                    title: 'Laughed Appropriately',
                    description: 'Used humor and laughter at the right moments',
                },
                humor: {
                    title: 'Used Humor',
                    description: 'Incorporated appropriate humor in conversation',
                },
            },
        },
        generalConductDetails: {
            title: ({ recipient }) => `Which aspect of ${recipient?.personalInfo?.firstName || 'their'}'s conduct do you want to praise?`,
            description: 'Select the specific aspect of their general conduct',
            validation: 'Please select a conduct aspect',
            options: {
                checkedFacts: {
                    title: 'Checked the facts before taking a stand and expressing an opinion',
                    description: '',
                },
                arrivedOnTime: {
                    title: 'Arrived on time to the meeting',
                    description: '',
                },
                involvedContributed: {
                    title: 'Was involved and contributed to the discussion',
                    description: '',
                },
                acknowledgedViewpoint: {
                    title: 'Made an effort to acknowledge the point of view of others',
                    description: '',
                },
                focusedConversation: {
                    title: 'Focused on the conversation and the speaker/s',
                    description: '',
                },
                keptTopic: {
                    title: 'Kept to the topic',
                    description: '',
                },
                keptTime: {
                    title: 'Kept to the alloted time',
                    description: '',
                },
                noInterrupting: {
                    title: 'Did not interrupt others',
                    description: '',
                },
            },
        },
        bodyLanguageDetails: {
            title: ({ recipient }) => `Which of ${recipient?.personalInfo?.firstName || 'their'}'s gestures does your praise relate to?`,
            description: 'Select the specific gesture or body language',
            validation: 'Please select a gesture',
            options: {
                handShake: {
                    title: 'Hand shake',
                    description: '',
                },
                namaste: {
                    title: 'Namaste',
                    description: '',
                },
                hug: {
                    title: 'Hug',
                    description: '',
                },
                thumbsUp: {
                    title: 'Thumbs up',
                    description: '',
                },
                handClap: {
                    title: 'Hand clap',
                    description: '',
                },
                highFive: {
                    title: 'High-five',
                    description: '',
                },
                smile: {
                    title: 'Smile',
                    description: '',
                },
            },
        },
        writtenMessagesDetails: {
            title: ({ recipient }) => `Which words or phrases did ${recipient?.personalInfo?.firstName || 'they'} use?`,
            description: 'Please describe what was written that you want to praise',
            placeholder: 'Try to quote exactly what was written...',
            validation: 'Please provide details about what was written',
        },
        writtenMessagesFramework: {
            title: 'In what framework were they written?',
            description: 'Select the medium through which the message was written',
            validation: 'Please select a framework',
        },
        verbalExpressionsDetails: {
            title: ({ recipient }) => `Which words or phrases did ${recipient?.personalInfo?.firstName || 'they'} use?`,
            description: 'Please describe what was said that you want to praise',
            placeholder: 'Try to quote exactly what was said...',
            validation: 'Please provide details about what was said',
        },
        // Schedule Agreement Details Step (Dynamic)
        scheduleAgreementDetails: {
            title: ({ answers }) => {
                const scheduleTypeToTitle = {
                    procedure: 'Which procedure are you referring to?',
                    'agreement-promise': 'What was promised or agreed upon?',
                    schedule: 'What was the agreed upon schedule?',
                    'goal-meeting': 'Which goal was met?',
                    generic: 'Please provide details',
                };
                const scheduleType = answers?.['schedule-agreement-type'];
                return scheduleTypeToTitle[scheduleType] || scheduleTypeToTitle.generic;
            },
            validation: 'Please provide details',
        },
        // Additional Effects Follow-up Step (Generic)
        additionalEffectsDetails: {
            title: ({ answers }) => {
                const selectedEffects = answers?.['additional-effects'];
                if (Array.isArray(selectedEffects)) {
                    const followUpOptions = [
                        'energizes',
                        'confidence',
                        'reputation',
                        'belonging',
                        'responsibility',
                    ];
                    const selectedFollowUps = selectedEffects.filter((effect) => followUpOptions.includes(effect));
                    if (selectedFollowUps.length > 0) {
                        // Sort effects in consistent order
                        const sortedEffects = selectedFollowUps.sort((a, b) => {
                            return followUpOptions.indexOf(a) - followUpOptions.indexOf(b);
                        });
                        // Map effect keys to human-readable names
                        const effectNames = {
                            energizes: 'energy',
                            confidence: 'confidence',
                            reputation: 'reputation',
                            belonging: 'belonging',
                            responsibility: 'responsibility',
                        };
                        // Build the effects list dynamically
                        const effectsText = sortedEffects
                            .map((effect) => effectNames[effect])
                            .join(', ');
                        // Handle single vs multiple effects
                        if (sortedEffects.length === 1) {
                            const singleEffectTitles = {
                                energizes: 'In relation to whom did this energize you?',
                                confidence: 'In relation to whom did this make you feel more confident?',
                                reputation: 'In whose eyes was your professional reputation raised?',
                                belonging: 'In relation to whom did this increase your sense of belonging?',
                                responsibility: 'In relation to whom did this empower you to take more responsibility?',
                            };
                            return (singleEffectTitles[sortedEffects[0]] ||
                                'In relation to whom do these positive effects apply?');
                        }
                        else {
                            // For multiple effects, build the title dynamically
                            const lastEffect = effectsText.lastIndexOf(', ');
                            const formattedEffects = lastEffect !== -1
                                ? effectsText.substring(0, lastEffect) +
                                    ' & ' +
                                    effectsText.substring(lastEffect + 2)
                                : effectsText;
                            return `In relation to whom did these effects (${formattedEffects}) apply?`;
                        }
                    }
                }
                // Fallback to generic title
                return 'In relation to whom do these positive effects apply?';
            },
            validation: 'Please select an option',
        },
        // Relation Options (shared across multiple follow-up questions)
        relationOptions: {
            everyone: 'Everyone',
            recipient: 'This person',
            employees: 'My employee(s)',
            customers: 'My customer(s)',
            suppliers: 'My supplier(s)',
            manager: 'My manager',
            team: 'The team we are both on',
            colleagues: 'My colleagues',
            seniorManagement: 'Senior management',
        },
        // Reputation specific options (slightly different wording)
        reputationOptions: {
            everyone: "Everyone's",
            recipient: "This person's",
            employees: "My employee's / employees'",
            customers: "My customer's / customers'",
            suppliers: "My supplier's / suppliers'",
            manager: "My manager's",
            team: "My and this person's team",
            colleagues: "My colleagues'",
            seniorManagement: "Senior management's",
        },
        praiseFeedbackFramework: {
            title: 'In what framework was the praise and feedback given?',
            description: 'Select the context where the praise and feedback was delivered',
            validation: 'Please select a framework',
        },
        success: {
            title: '🎉 Thank you for sharing!',
            description: 'Your praise and feedback have been recorded successfully.',
        },
        review: {
            goal: {
                title: {
                    creator: 'The impact of the event on your success',
                    receiver: 'The impact of the event on {{employeeName}} success',
                },
                prefixBeforeSend: {
                    creator: 'Your chances of achieving the goal',
                    receiver: '{{employeeName}} changes of achieving the goal',
                },
                prefixAfterSend: 'The chances of {{employeeName}} achieving the goal',
                affection: 'increased',
            },
            title: {
                creator: 'I See U',
                receiver: 'Great job {{employeeName}}!',
            },
            subtitle: {
                creator: 'You have initiated a conversation with {{employeeName}}',
                receiver: 'You have received an "I See U" from {{employeeName}}',
            },
            description: 'We invite you to read here the details of what you’ve been praised for.\nWe appreciate your actions which have greatly contributed to our overall success!',
            date: 'The event you want to talk about took place on',
            reason: 'The event is about',
            additionalEffectsTitle: 'Further Affect',
            companyValues: 'Contribution to company values',
            edit: 'Edit',
            saveDraft: 'Save as Draft',
            send: 'Send',
            sending: 'Sending...',
            reasonDetails: 'I See U Details',
            reasonPrefixes: {
                'collaboration-service': 'A response/service received from',
                'schedule-agreement': 'A schedule/agreement issue with',
                communication: 'A communication issue with',
                'information-sharing': 'Information sharing with',
                'decision-making': 'A decision-making process with',
                reporting: 'A reporting issue with',
                'other-information': 'Other information from',
                'expectations-setting': 'Expectations setting with',
            },
            additionalEffectsPrefix: 'Make you feel',
            additionalEffectsMap: {
                energizes: 'energizes',
                confidence: 'confidence',
                connection: 'connection',
                reputation: 'reputation',
                meaningful: 'meaningful',
            },
        },
    },
    // Feedback Questions Wizard (unique feedback-specific content)
    feedbackQuestions: {
        title: 'Feedback & Development Wizard',
        description: 'Share constructive feedback for workplace improvement',
        feedbackSubmittedSuccessfully: 'Feedback submitted successfully',
        feedbackSubmittedFailed: 'Failed to submit feedback',
        // Praise/Feedback Recipient Selection for Feedback Wizard
        // Praise/Feedback Employee Selection for Feedback Wizard
        praiseFeedbackEmployeeSelection: {
            title: 'Who?',
            placeholder: 'Select employees who should have provided feedback',
            validation: 'Please select at least one employee',
        },
        praiseFeedbackRecipient: {
            title: ({ recipient, answers, }) => {
                const recipientName = recipient?.personalInfo?.firstName || 'this person';
                const praiseFeedbackSubject = answers?.['praise-feedback-subject-selection'];
                switch (praiseFeedbackSubject?.[0]) {
                    case 'praise-expected-not-received':
                        return `Who did you expect ${recipientName} to give I See U to?`;
                    case 'feedback-expected-not-received':
                        return `Who did you expect ${recipientName} to give I Need U to?`;
                    case 'inappropriate-feedback-received':
                        return `Who received inappropriate I Need U from ${recipientName}?`;
                    default:
                        return `Who received problem with the I See U or I Need U from ${recipientName}?`;
                }
            },
        },
        // Praise/Feedback Subject Description for Feedback Wizard
        praiseFeedbackSubjectDescription: {
            title: ({ recipient, answers, }) => {
                const recipientName = recipient?.personalInfo?.firstName || 'this person';
                const praiseFeedbackSubject = answers?.['praise-feedback-subject-selection'];
                switch (praiseFeedbackSubject?.[0]) {
                    case 'praise-expected-not-received':
                        return `Describe the I See U you expected ${recipientName} to give`;
                    case 'feedback-expected-not-received':
                        return `Describe the I Need U you expected ${recipientName} to give`;
                    case 'inappropriate-feedback-received':
                        return `Describe the inappropriate I Need U you received`;
                    default:
                        return `Describe the subject for which you expected I See U or I Need U`;
                }
            },
            description: 'Write briefly',
            validation: 'Please describe the subject',
        },
        collaborationServiceType: {
            title: 'Please select a subject',
            validation: 'Please select a type',
            options: {
                serviceNotReceived: {
                    title: 'Response/Service Not Received',
                    description: 'Expected service but did not receive it',
                },
                serviceReceived: {
                    title: 'Response/Service Received',
                    description: "Received service but didn't solve my issue",
                },
                collaborationNotHappened: {
                    title: ({ recipient }) => `There was no collaboration with ${recipient?.personalInfo?.firstName || 'this person'}`,
                    description: 'Expected collaboration but it did not occur',
                },
                collaborationHappened: {
                    title: ({ recipient }) => `Collaboration with ${recipient?.personalInfo?.firstName || 'this person'}`,
                    description: "Collaboration occurred but didn't solve my issue",
                },
            },
        },
        scheduleAgreementType: {
            title: 'Please select a subject',
            validation: 'Please select a type',
            options: {
                goalNotMet: {
                    title: 'Not meeting a goal',
                    description: 'A goal or objective was not achieved',
                },
                scheduleNotKept: {
                    title: 'Not keeping to the schedule',
                    description: 'Deadlines or time commitments were missed',
                },
                agreementNotKept: {
                    title: 'Not keeping to an agreement or promise',
                    description: 'Promises or commitments were not followed through',
                },
                procedureNotFollowed: {
                    title: 'Not complying with procedures',
                    description: 'Established procedures were not adhered to',
                },
            },
        },
        communicationType: {
            title: 'Please select a subject',
            validation: 'Please select a type',
            options: {
                speechTone: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'this person'}'s style of speech and tone`,
                    description: 'How they spoke and their tone of voice',
                },
                generalConduct: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'this person'}'s general conduct during the discussion`,
                    description: 'Their behavior during conversations',
                },
                bodyLanguage: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'this person'}'s gestures`,
                    description: 'body lanugage, facial expression etc.',
                },
                writtenMessages: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'this person'}'s Written Messages`,
                    description: 'Things written in emails, messages, etc.',
                },
                verbalExpressions: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'this person'}'s Verbal Expressions`,
                    description: 'Things said verbally',
                },
            },
        },
        frameworkSelection: {
            title: ({ answers, recipient, }) => {
                // Check which flow we're in based on previous steps
                if (answers?.['reporting-feedback-subject']) {
                    return 'In which framework was the report received?';
                }
                if (answers?.['other-information-feedback-subject']) {
                    return 'In which framework was the information shared?';
                }
                if (answers?.['collaboration-service-type']) {
                    return 'How should the service have been handled?';
                }
                if (answers?.['communication-type-selection']) {
                    return 'How should the communication have been handled?';
                }
                if (answers?.['expectations-setting-subject-selection']) {
                    return `In which framework did ${recipient?.personalInfo?.firstName || 'they'} set expectations?`;
                }
                // Check if this is from the praise wizard expectations flow
                if (answers?.['reason-selection']?.[0] === 'meeting-expectations') {
                    return 'In which framework did should the expectations have been set?';
                }
                // Fallback to generic
                return 'How should this have been handled?';
            },
        },
        serviceFrameworkSelection: {
            title: 'In which framework did this happen?',
            description: 'Select the context where the service issue occurred',
            validation: 'Please select a framework',
        },
        // Feedback-specific descriptions for common reason options
        reasonSelection: {
            title: ({ recipient }) => `What does the event with ${recipient?.personalInfo?.firstName || 'this person'} is about?`,
            validation: 'Please select a reason',
            options: {
                collaboration: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'}'s collaboration / ${recipient?.personalInfo?.firstName || 'this person'}'s response/ service`,
                },
                scheduleAgreement: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'} not keeping to the schedule / not keeping to an agreement / not complying with procedures / not meeting a goal`,
                },
                communication: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'}'s communication style (the manner of speech, writing or behavior)`,
                },
                informationSharing: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'} sharing information / reporting / involving me in the decision-making process`,
                },
                praiseFeedback: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'}'s conduct while giving I See U or I Need U`,
                },
                meetingExpectations: {
                    title: ({ recipient }) => `${recipient?.personalInfo?.firstName || 'This person'}'s conduct while setting expectations`,
                },
            },
        },
        goalSelection: {
            title: ({ recipient, answers, }) => {
                const reasonToTitle = {
                    communication: `Which one of your goals is impacted by ${recipient?.personalInfo?.firstName || 'this person'}'s communication style during the meeting?`,
                    'expectations-setting': `Which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'}'s conduct in relation to setting expectations?`,
                    default: `Which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'}?`,
                };
                const scheduleAgreementTypeToTitle = {
                    'goal-not-met': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'} not meeting the goal?`,
                    'schedule-not-kept': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'} not keeping to the schedule`,
                    'agreement-not-kept': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'} not keeping to the agreement or promise?`,
                    'procedure-not-followed': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'} not complying with the procedures?`,
                };
                const collaborationServiceTypeToTitle = {
                    'service-not-received': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'} not responding/providing a service?`,
                    'service-received': `which one of your goals does ${recipient?.personalInfo?.firstName || 'this person'}'s response/service hurt?`,
                    'collaboration-not-happened': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'} not collaborating?`,
                    'collaboration-happened': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'}'s collaboration?`,
                };
                const informationSharingTypeToTitle = {
                    'decision-making-involvement': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'}'s conduct in relation to the decision-making process?`,
                    reporting: `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'}'s conduct in relation to reporting?`,
                    'other-information-sharing': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'}'s conduct in relation to other information sharing?`,
                };
                const praiseFeedbackTypeToTitle = {
                    'praise-expected-not-received': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'}'s lack of I See U?`,
                    'feedback-expected-not-received': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'}'s lack of I Need U?`,
                    'inappropriate-feedback-received': `which one of your goals is hurt by ${recipient?.personalInfo?.firstName || 'this person'}'s inappropriate I Need U?`,
                };
                if (answers?.['reason-selection']?.[0] === 'collaboration-service') {
                    return collaborationServiceTypeToTitle[answers?.['collaboration-service-type']?.[0]];
                }
                if (answers?.['reason-selection']?.[0] === 'schedule-agreement') {
                    return scheduleAgreementTypeToTitle[answers?.['schedule-agreement-type']?.[0]];
                }
                if (answers?.['reason-selection']?.[0] === 'information-sharing') {
                    return informationSharingTypeToTitle[answers?.['information-sharing-subject-selection']?.[0]];
                }
                if (answers?.['reason-selection']?.[0] === 'praise-feedback') {
                    return praiseFeedbackTypeToTitle[answers?.['praise-feedback-subject-selection']?.[0]];
                }
                return (reasonToTitle[answers?.['reason-selection']?.[0]] || reasonToTitle.default);
            },
            validation: 'Please select a goal',
        },
        goalImpactRange: {
            title: 'To what extent are the chances of achieving your goal reduced?',
            validation: 'Please rate the impact',
            options: {
                relativelyReduced: {
                    title: 'Relatively reduced',
                },
                significantlyReduced: {
                    title: 'Significantly reduced',
                },
                extremelyReduced: {
                    title: 'Extremely reduced',
                },
            },
        },
        additionalEffectsTemp: {
            title: 'In which other ways does this event affect you?',
            validation: 'Please select at least one effect',
            options: {
                [EffectSelectionOptions.ENERGY]: {
                    title: 'Causes me to expand unnecessary energy',
                    beforeSend: (name, impacted) => `${name} causes you to expend unnecessary energy in relation to ${impacted}.`,
                    afterSend: (name, impacted) => `You cause ${name} to expend unnecessary energy in relation to ${impacted}.`,
                },
                [EffectSelectionOptions.CONFIDENCE]: {
                    title: 'Lowers my confidence',
                    beforeSend: (name, impacted) => `${name} lowers your confidence in relation to ${impacted}.`,
                    afterSend: (name, impacted) => `You lower ${name}'s confidence in relation to ${impacted}.`,
                },
                [EffectSelectionOptions.REPUTATION]: {
                    title: 'Damages my reputation',
                    beforeSend: (name, impacted) => `${name} damages your reputation in the eyes of ${impacted}.`,
                    afterSend: (name, impacted) => `You damage ${name}'s reputation in the eyes of ${impacted}.`,
                },
                [EffectSelectionOptions.BELONGING]: {
                    title: 'Lowers my sense of belonging',
                    beforeSend: (name, impacted) => `${name} lowers your sense of belonging in relation to ${impacted}.`,
                    afterSend: (name, impacted) => `You lower ${name}'s sense of belonging in relation to ${impacted}.`,
                },
                [EffectSelectionOptions.RESPONSIBILITY]: {
                    title: 'Hurts my ability to initiate and take on responsibility',
                    beforeSend: (name, impacted) => `${name} hurts your ability to initiate and take on responsibility in relation to ${impacted}.`,
                    afterSend: (name, impacted) => `You hurt ${name}'s ability to initiate and take on responsibility in relation to ${impacted}.`,
                },
                [EffectSelectionOptions.LEARNING]: {
                    title: 'Hurts my ability to learn and develop myself',
                    beforeSend: (name) => `${name} hurts your ability to learn and develop yourself.`,
                    afterSend: (name) => `You hurt ${name}'s ability to learn and develop.`,
                },
                [EffectSelectionOptions.NO_OTHER_EFFECTS]: {
                    title: 'Does not affect me in other ways',
                },
                [EffectSelectionOptions.CONNECTION]: {
                    title: 'Makes us feel less connected',
                    beforeSend: (name) => `You feel less connected to ${name}.`,
                    afterSend: (name) => `You make ${name} feel less connected to you.`,
                },
                [EffectSelectionOptions.MEANINGFUL]: {
                    title: 'Makes me feel less meaningful',
                    beforeSend: (name) => `${name} makes you feel less meaningful.`,
                    afterSend: (name) => `You make ${name} feel less meaningful.`,
                },
            },
        },
        additionalEffects: {
            title: 'In which other ways does this event affect you?',
            validation: 'Please select at least one effect',
            options: {
                [EffectSelectionOptions.ENERGY]: {
                    title: 'Causes me to expand unnecessary energy',
                    beforeSend: (name) => `${name} causes you to expend unnecessary energy.`,
                    afterSend: (name) => `You cause ${name} to expend unnecessary energy.`,
                },
                [EffectSelectionOptions.CONFIDENCE]: {
                    title: 'Lowers my confidence',
                    beforeSend: (name) => `${name} lowers your confidence.`,
                    afterSend: (name) => `You lower ${name}'s confidence.`,
                },
                [EffectSelectionOptions.REPUTATION]: {
                    title: 'Damages my reputation',
                    beforeSend: (name) => `${name} damages your reputation.`,
                    afterSend: (name) => `You damage ${name}'s reputation.`,
                },
                [EffectSelectionOptions.BELONGING]: {
                    title: 'Lowers my sense of belonging',
                    beforeSend: (name) => `${name} lowers your sense of belonging.`,
                    afterSend: (name) => `You lower ${name}'s sense of belonging.`,
                },
                [EffectSelectionOptions.RESPONSIBILITY]: {
                    title: 'Hurts my ability to initiate and take on responsibility',
                    beforeSend: (name) => `${name} hurts your ability to initiate and take on responsibility.`,
                    afterSend: (name) => `You hurt ${name}'s ability to initiate and take on responsibility.`,
                },
                [EffectSelectionOptions.LEARNING]: {
                    title: 'Hurts my ability to learn and develop myself',
                    beforeSend: (name) => `${name} hurts your ability to learn and develop yourself.`,
                    afterSend: (name) => `You hurt ${name}'s ability to learn and develop.`,
                },
                [EffectSelectionOptions.NO_OTHER_EFFECTS]: {
                    title: 'Does not affect me in other ways',
                },
                [EffectSelectionOptions.CONNECTION]: {
                    title: 'Makes us feel less connected',
                    beforeSend: (name) => `You feel less connected to ${name}.`,
                    afterSend: (name) => `You make ${name} feel less connected to you.`,
                },
                [EffectSelectionOptions.MEANINGFUL]: {
                    title: 'Makes me feel less meaningful',
                    beforeSend: (name) => `${name} makes you feel less meaningful.`,
                    afterSend: (name) => `You make ${name} feel less meaningful.`,
                },
            },
        },
        // Negative Effects Follow-up Step (Generic)
        additionalEffectsDetails: {
            title: ({ answers }) => {
                const selectedEffects = answers?.['additional-effects'];
                if (Array.isArray(selectedEffects) && selectedEffects.length > 0) {
                    const selectedEffectsArray = selectedEffects;
                    const followUpOptions = [
                        EffectSelectionOptions.ENERGY,
                        EffectSelectionOptions.CONFIDENCE,
                        EffectSelectionOptions.REPUTATION,
                        EffectSelectionOptions.BELONGING,
                        EffectSelectionOptions.RESPONSIBILITY,
                    ];
                    const selectedFollowUps = selectedEffectsArray.filter((effect) => followUpOptions.includes(effect));
                    if (selectedFollowUps.length > 0) {
                        // Sort effects in consistent order to match i18n keys
                        const sortedEffects = selectedFollowUps.sort((a, b) => {
                            return followUpOptions.indexOf(a) - followUpOptions.indexOf(b);
                        });
                        // Create a comma-separated list of effect keys for i18n lookup
                        const effectsList = sortedEffects.join(',');
                        const effectsToTitle = {
                            [EffectSelectionOptions.ENERGY]: 'In relation to whom do you feel you had to invest unnecessary energy?',
                            [EffectSelectionOptions.CONFIDENCE]: 'In relation to whom did you lose confidence?',
                            [EffectSelectionOptions.REPUTATION]: 'In whose eyes was your reputation damaged?',
                            [EffectSelectionOptions.BELONGING]: 'In relation to whom was your sense of belonging harmed?',
                            [EffectSelectionOptions.RESPONSIBILITY]: 'In relation to whom was your ability to take responsibility harmed?',
                            'energy,confidence': 'In relation to whom did you have to invest unnecessary energy and lose confidence?',
                            'energy,reputation': 'In relation to whom did you have to invest unnecessary energy and your reputation was damaged?',
                            'energy,belonging': 'In relation to whom did you have to invest unnecessary energy and your sense of belonging was harmed?',
                            'energy,responsibility': 'In relation to whom did you have to invest unnecessary energy and your ability to take responsibility was harmed?',
                            'confidence,reputation': 'In relation to whom did you lose confidence and your reputation was damaged?',
                            'confidence,belonging': 'In relation to whom did you lose confidence and your sense of belonging was harmed?',
                            'confidence,responsibility': 'In relation to whom did you lose confidence and your ability to take responsibility was harmed?',
                            'reputation,belonging': 'In relation to whom was your reputation damaged and your sense of belonging harmed?',
                            'reputation,responsibility': 'In relation to whom was your reputation damaged and your ability to take responsibility harmed?',
                            'belonging,responsibility': 'In relation to whom was your sense of belonging and ability to take responsibility harmed?',
                            'energy,confidence,reputation': 'In relation to whom were you affected by these issues?',
                            'energy,confidence,belonging': 'In relation to whom were you affected by these issues?',
                            'energy,confidence,responsibility': 'In relation to whom were you affected by these issues?',
                            'energy,reputation,belonging': 'In relation to whom were you affected by these issues?',
                            'energy,reputation,responsibility': 'In relation to whom were you affected by these issues?',
                            'energy,belonging,responsibility': 'In relation to whom were you affected by these issues?',
                            'confidence,reputation,belonging': 'In relation to whom were you affected by these issues?',
                            'confidence,reputation,responsibility': 'In relation to whom were you affected by these issues?',
                            'confidence,belonging,responsibility': 'In relation to whom were you affected by these issues?',
                            'reputation,belonging,responsibility': 'In relation to whom were you affected by these issues?',
                            generic: 'In relation to whom were you affected?',
                        };
                        return effectsToTitle[effectsList] || effectsToTitle.generic;
                    }
                }
                // Fallback to generic title
                return 'In relation to whom were you affected?';
            },
            validation: 'Please select an option',
        },
        // Relation Options (shared across negative effects follow-up questions)
        relationOptions: {
            everyone: 'Everyone',
            recipient: 'The person involved',
            employees: 'My employees',
            customers: 'My customers',
            suppliers: 'My suppliers',
            manager: 'My manager',
            team: 'Our team',
            colleagues: 'My colleagues',
            seniorManagement: 'Senior management',
        },
        collaborationServiceDescription: {
            title: ({ answers }) => {
                const serviceTypeToTitle = {
                    'service-not-received': 'Describe the response/service you were expecting',
                    'service-received': 'Describe the response/service received',
                    'collaboration-not-happened': 'Describe the type of collaboration',
                    'collaboration-happened': 'What happened during the collaboration?',
                };
                const serviceType = answers?.['collaboration-service-type'];
                return serviceTypeToTitle[serviceType];
            },
            validation: 'Please provide details about the situation',
        },
        scheduleAgreementDescription: {
            title: ({ answers }) => {
                const scheduleTypeToTitle = {
                    'goal-not-met': 'Which goal was not met?',
                    'schedule-not-kept': 'What was agreed upon schedule?',
                    'agreement-not-kept': 'What was promised or agreed upon?',
                    'procedure-not-followed': 'Describe the procedure',
                };
                const scheduleType = answers?.['schedule-agreement-type'];
                return (scheduleTypeToTitle[scheduleType] ||
                    'Provide details about what happened');
            },
            validation: 'Please describe what happened',
        },
        verbalExpressionsDescription: {
            title: ({ recipient }) => `What words or phrases did ${recipient?.personalInfo?.firstName || 'they'} use?`,
            placeholder: 'Describe what was said...',
            validation: 'Please describe what was said',
        },
        writtenMessagesDescription: {
            title: ({ recipient }) => `What words or phrases did ${recipient?.personalInfo?.firstName || 'they'} use?`,
            placeholder: 'Describe what was written...',
            validation: 'Please describe what was written',
        },
        bodyLanguageDetails: {
            title: ({ recipient }) => `Which of ${recipient?.personalInfo?.firstName || 'their'}'s gestures do you want to comment on?`,
            validation: 'Please select a gesture',
            options: {
                lookOfSurprise: { title: 'Look of surprise' },
                lookOfDisrespect: { title: 'Look of disrespect' },
                offendedLook: { title: 'Offended look' },
                staring: { title: 'Staring' },
                angeredLook: { title: 'Angered look' },
                hittingTable: { title: 'Hitting the table' },
                headHolding: { title: 'Head holding' },
                waiveDismissal: { title: 'Waive of dismissal' },
                quickSharpMovements: { title: 'Quick and sharp movements' },
                foldedArms: { title: 'Folded arms' },
            },
        },
        speechToneDetails: {
            title: ({ recipient }) => `Which aspect of ${recipient?.personalInfo?.firstName || 'their'}'s speech style and tone does your feedback relate to?`,
            validation: 'Please select a speech aspect',
            options: {
                laughingInappropriately: { title: 'Laughing inappropriately' },
                speakingQuietly: { title: 'Speaking too quietly' },
                speakingFast: { title: 'Speaking too fast' },
                suddenVoiceRaise: { title: 'Sudden raise of voice' },
                snickering: { title: 'Snickering' },
                highTone: { title: 'Speaking with a relatively high tone' },
            },
        },
        generalConductDetails: {
            title: ({ recipient }) => `Which aspect of ${recipient?.personalInfo?.firstName || 'their'} conduct does your feedback relate to?`,
            validation: 'Please select a conduct aspect',
            options: {
                leavingEarly: { title: 'Leaving before the end of the meeting' },
                arrivingLate: { title: 'Arriving after the meeting started' },
                interruptingOthers: { title: 'Interrupting others' },
                speakingTooLong: { title: 'Speaking longer than the allotted time' },
                switchingTopics: { title: 'Switching topics frequently' },
                notFocusing: {
                    title: 'Focusing on matters other than the conversation and the speaker/s',
                },
                reducingOthersExpression: {
                    title: 'Reducing the opportunity for others to express themselves',
                },
                prematureConclusions: {
                    title: 'Arriving at conclusions before having all the facts',
                },
                notInvolvedEnough: {
                    title: 'Not being involved enough in the discussion',
                },
                arguingTooMuch: {
                    title: 'Arguing and being too concerned with who is right',
                },
            },
        },
        praiseFeedbackSubjectSelection: {
            title: ({ recipient }) => `What does your I Need U to ${recipient?.personalInfo?.firstName || 'them'} relate to?`,
            validation: 'Please select a I See U or I Need U subject',
            options: {
                praiseExpectedNotReceived: {
                    title: 'I See U I or other employees expected but did not receive',
                    description: 'Expected I See U that was not given',
                },
                inappropriateFeedbackReceived: {
                    title: 'Inappropriate I Need U I or other employees received',
                    description: 'I Need U that was given but was not appropriate',
                },
                feedbackExpectedNotReceived: {
                    title: 'I Need U I or other employees expected but did not receive',
                    description: 'Expected I Need U that was not given',
                },
            },
        },
        informationSharingSubjectSelection: {
            title: 'What sort of information sharing was it?',
            validation: 'Please select an information sharing type',
            options: {
                decisionMakingInvolvement: {
                    title: 'Involving me in the decision-making process in the area of my responsibility',
                    description: 'Issues with involvement in decision-making',
                },
                reporting: {
                    title: 'Reporting',
                    description: 'Issues with reporting or receiving reports',
                },
                otherInformationSharing: {
                    title: 'Sharing other information',
                    description: 'Issues with sharing other types of information',
                },
            },
        },
        decisionMakingFeedbackSubject: {
            title: 'What does your feedback relate to?',
            validation: 'Please select a decision-making issue',
            options: {
                decisionNotInvolving: {
                    title: 'I was not invloved in the decision-making process',
                    description: 'A decision was made without your involvement',
                },
                partialDecisionInvolving: {
                    title: 'I was partially invloved in in the decision-making process',
                    description: 'You were involved but not fully in the decision-making',
                },
            },
        },
        decisionMakingDetails: {
            title: 'Describe the decision made',
            validation: 'Please describe the decision-making details',
        },
        reportingFeedbackSubject: {
            title: 'What does your feedback relate to?',
            validation: 'Please select a reporting issue',
            options: {
                reportNotReceived: {
                    title: 'I did not receive the report',
                    description: 'A report you expected was not received',
                },
                reportReceivedLate: {
                    title: 'I received the report late',
                    description: 'The report was received but was late',
                },
                reportPartialReceived: {
                    title: 'I received only a partial report',
                    description: 'Only part of the expected report was received',
                },
                reportReceivedAfterOthers: {
                    title: 'I received the report after it had been given to someone else',
                    description: 'The report was given to others before you received it',
                },
            },
        },
        reportingDetails: {
            title: 'Describe the subject of the report',
            validation: 'Please describe the reporting details',
        },
        reportingFramework: {
            title: 'How should the reporting have been handled?',
            validation: 'Please select a framework',
        },
        otherInformationFeedbackSubject: {
            title: 'What does your feedback relate to?',
            validation: 'Please select an information sharing issue',
            options: {
                informationNotReceived: {
                    title: 'Not receiving the information',
                    description: 'Information you expected was not received',
                },
                informationPartialReceived: {
                    title: 'Receiving partial information',
                    description: 'Only part of the expected information was received',
                },
                informationReceivedDelayed: {
                    title: 'Delay in receiving the information',
                    description: 'Information was received but with a delay',
                },
                informationReceivedAfterOthers: {
                    title: 'Receiving the information after it had been given to someone else',
                    description: 'Information was given to others before you received it',
                },
            },
        },
        otherInformationDetails: {
            title: ({ answers, }) => {
                if (answers?.['other-information-feedback-subject']?.[0] ===
                    'information-not-received') {
                    return 'Describe the information that was not shared';
                }
                return 'Describe the information shared';
            },
            validation: 'Please describe the information sharing details',
        },
        otherInformationFramework: {
            title: 'How should the information sharing have been handled?',
            validation: 'Please select a framework',
        },
        expectationsSettingSubjectSelection: {
            title: 'What does your feedback relate to?',
            validation: 'Please select an expectations issue',
            options: {
                partialExpectationsSet: {
                    title: 'The expectations set with me were partial',
                    description: 'Expectations were set but were incomplete',
                },
                noExpectationsSet: {
                    title: 'Expectations were not set with me',
                    description: 'No expectations were set at all',
                },
            },
        },
        expectationsSettingDetails: {
            title: ({ recipient, answers, }) => {
                if (answers?.['expectations-setting-subject-selection']?.[0] ===
                    'partial-expectations-set') {
                    return `In which area did ${recipient?.personalInfo?.firstName || 'they'} set expectations?`;
                }
                if (answers?.['expectations-setting-subject-selection']?.[0] ===
                    'no-expectations-set') {
                    return `Which expectations should have been set?`;
                }
                return `In which area did ${recipient?.personalInfo?.firstName || 'they'} set expectations?`;
            },
            validation: 'Please describe the expectations setting details',
        },
        stakeholderSelection: {
            title: 'In relation to whom?',
            validation: 'Please select stakeholders',
            options: {
                everyone: {
                    title: 'Everyone',
                    description: 'In general, in relation to everyone',
                },
                recipient: {
                    title: 'The person involved',
                    description: 'In relation to the feedback recipient',
                },
                myEmployees: {
                    title: 'My employees',
                    description: 'In relation to people who report to me',
                },
                myManager: {
                    title: 'My manager',
                    description: 'In relation to my direct supervisor',
                },
                colleagues: {
                    title: 'My colleagues',
                    description: 'In relation to my peers and coworkers',
                },
                mySuppliers: {
                    title: 'My suppliers/vendors',
                    description: 'In relation to my vendors/suppliers',
                },
                myCustomers: {
                    title: 'My customers/clients',
                    description: 'In relation to external customers or clients',
                },
                seniorManagement: {
                    title: 'Senior management',
                    description: 'In relation to executive leadership',
                },
                ourTeam: {
                    title: 'The team we are both on',
                    description: 'In relation to our shared team',
                },
                myColleagues: {
                    title: 'My colleagues',
                    description: 'In relation to my peers and coworkers',
                },
            },
        },
        organizationalImpactRange: {
            title: 'To what extent does this impact the organization?',
            description: 'Rate the organizational impact of this issue',
            options: {
                aLittle: {
                    title: 'A little',
                    description: 'Minor organizational impact',
                },
                somewhat: {
                    title: 'Somewhat',
                    description: 'Moderate organizational impact',
                },
                quiteABit: {
                    title: 'Quite a bit',
                    description: 'Significant organizational impact',
                },
                aLot: {
                    title: 'A lot',
                    description: 'Major organizational impact',
                },
            },
        },
        companyValues: {
            title: 'Does this event compromise company values?',
        },
        success: {
            title: '🎯 Thank you for your feedback!',
            description: 'Your constructive feedback has been recorded and will help improve our workplace.',
        },
        review: {
            goal: {
                title: {
                    creator: 'The impact of the event on your success',
                    receiver: 'The impact of the event on {{employeeName}} success',
                },
                prefixBeforeSend: {
                    creator: 'Your chances of achieving the goal',
                    receiver: '{{employeeName}} chances of achieving the goal',
                },
                prefixAfterSend: 'The chances of {{employeeName}} achieving the goal',
                affection: 'decreased',
            },
            title: {
                creator: 'I Need U',
                receiver: 'I Need U',
            },
            subtitle: {
                creator: 'You have initiated a conversation with {{employeeName}}',
                receiver: 'You have received an "I Need U" from {{employeeName}}',
            },
            description: '',
            date: 'The event you want to talk about took place on',
            reason: 'The event is about',
            additionalEffectsTitle: 'Additional Effects',
            companyValues: 'Compromised company values',
            edit: 'Edit',
            saveDraft: 'Save as Draft',
            send: 'Send',
            sending: 'Sending...',
            reasonDetails: 'I Need U Details',
            reasonPrefixes: {
                'collaboration-service': 'A response/service issue with',
                'schedule-agreement': 'A schedule/agreement issue with',
                communication: 'A communication issue with',
                'information-sharing': 'Information sharing issue with',
                'decision-making': 'A decision-making process issue with',
                reporting: 'A reporting issue with',
                'other-information': 'Other information issue with',
                'expectations-setting': 'Expectations setting issue with',
            },
            additionalEffectsMap: {
                frustrated: 'frustrated',
                confused: 'confused',
                disappointed: 'disappointed',
                concerned: 'concerned',
                worried: 'worried',
                stressed: 'stressed',
            },
            additionalEffectsPrefix: 'Caused you to feel',
        },
    },
    debriefQuestions: {
        type: {
            title: 'What type of debrief is this?',
            personal: 'Personal debrief',
            group: 'Group debrief',
        },
        debriefSubmittedSuccessfully: 'Debrief submitted successfully',
        debriefSubmittedFailed: 'Failed to submit debrief',
        signalsUpdatedSuccessfully: 'Signals updated successfully',
        signalUpdateFailed: 'Failed to update signals',
        title: 'Debrief Wizard',
        description: 'here you will be able to debrief an event',
        debriefLeader: {
            title: 'who is the leader of the debrief?',
            description: 'who was the leader of the debrief?',
        },
        debriefGoalSelection: {
            title: 'which goal was impacted by the event?',
        },
        debriefCrucialParticipants: {
            title: 'who are the core participants in the event that the debrief is about?',
        },
        debriefNonCrucialParticipants: {
            title: 'who are the non-core participants in the event that the debrief is about?',
        },
        debriefViewers: {
            title: 'who are the viewers of the debrief?',
        },
        expectedResult: {
            title: 'what was the expected result you want to debrief?',
            achievedResult: {
                title: 'Achieved result',
            },
            notAchievedResult: {
                title: 'Unachieved result',
            },
            textAreaLabel: 'Additional Details',
        },
        facts: {
            title: 'what are the facts of the event that the debrief is about?',
            description: 'please add the events in chronological order',
        },
        topFacts: {
            title: 'what are the top facts of the event that the debrief is about?',
        },
        matchPoint: {
            whatDidYouDo: {
                title: 'which is the one significant action, that you performed or did not performed, that affected the match point?',
                iDid: 'I Did',
                iDidnt: "I Didn't",
            },
            actions: {
                title: 'Select actions',
                setClearGoals: 'Set clear goals',
                defineResponsiblePerson: 'Take action to determine a leader/ responsible person',
                performPreparation: 'Full preparation',
                sharedInformation: 'Share the information with the relevant people',
                actInResponseToInteraction: 'Act in response to information/ feedback/ request',
                workAccordingToProcedure: 'Act according to procedures/ policies/ work processes',
                matchingExpectations: 'Manage expectations',
                improvisation: 'Monitor and control',
            },
            why: {
                title: 'Why did you operate in such a way?',
                skipText: 'I dont know why',
                placeholder: 'Write here...',
            },
            reason: {
                title: 'And your reason?',
            },
            assumption: {
                title: 'What was your assumption that caused you to act like that?',
            },
            signal: {
                title: 'What is the signal?',
                placeholder: 'Write your signal here...',
                addMore: 'Add another signal',
                continue: 'Continue to next match point',
                finish: 'Finish',
            },
        },
        review: {
            title: 'Summary',
            description: 'Review and edit the team members inputs for each match point',
            matchPoint: 'Match point {{number}}',
            edit: 'Edit',
            saveDraft: 'Save Draft',
            send: 'Send Debrief',
            sending: 'Sending...',
            achievedResult: 'Achieved result',
            notAchievedResult: 'Unachieved result',
        },
        viewers: {
            selectViewers: 'Select Viewers',
            updateFailed: 'Failed to update viewers',
            updatedSuccessfully: 'Viewers updated successfully',
        },
    },
    stepTitles: {
        'employee-selection': 'Employee Selection',
        'date-selection': 'Date Selection',
        'reason-selection': 'Reason Selection',
        'goal-selection': 'Goal Selection',
        'goal-impact-range': 'Goal Impact',
        'company-values': 'Company Values',
        'additional-effects': 'Additional Effects',
        'additional-effects-details': 'Additional Effects Details',
        'additional-negative-effects': 'Additional Negative Effects',
        'negative-effects-details': 'Negative Effects Details',
        'organizational-impact-range': 'Organizational Impact',
        'collaboration-service-details': 'Collaboration Service Details',
        'communication-details': 'Communication Details',
        [SharedReasonOptions.COLLABORATION_SERVICE]: 'Collaboration Service Details',
        [SharedReasonOptions.SCHEDULE_AGREEMENT]: 'Schedule Agreement Details',
        [SharedReasonOptions.COMMUNICATION]: 'Communication Details',
        [SharedReasonOptions.INFORMATION_SHARING]: 'Information Sharing Details',
        [SharedReasonOptions.PRAISE_FEEDBACK]: 'I see U & I need U Details',
        [FeedbackReasonOptions.EXPECTATIONS_SETTING]: 'Expectations Setting Details',
        [PraiseReasonOptions.MEETING_EXPECTATIONS]: 'Meeting Expectations Details',
        'schedule-agreement-details': 'Schedule Agreement Details',
        'manager-involvement-details': 'Manager Involvement Details',
        participants: 'Participants',
        'bot-invite': 'Bot Invite',
        'match-points': 'Match Points',
        facts: 'Facts',
        emotions: 'Emotions',
        assumptions: 'Assumptions',
        signals: 'Signals',
        'reason-details': 'Selected Reason Details',
    },
    aiTextArea: {
        input: 'Your input',
        enrichButton: 'Enrich with AI',
        enrichInProgress: 'Enriching...',
        aiVersion: 'AI Enriched Version',
        useAIVersion: 'Use this version',
        discard: 'Discard',
        explanation: 'Explanation:',
        error: 'Failed to enrich feedback. Please try again.',
    },
    common: {
        rangeDefaults: {
            relatively: 'Relatively',
            significantly: 'Significantly',
            extremely: 'Extremely',
        },
        buttons: {
            skip: 'Skip',
            previous: 'Previous',
            closeDebrief: 'Finish Debrief',
            next: 'Next',
            continue: 'Continue',
            save: 'Save',
            saving: 'Saving...',
            cancel: 'Cancel',
            startBot: 'Start Bot',
            startWizard: 'Start Wizard',
            addAnotherSignal: '+ Add another signal',
            saveSignals: 'Save Signals',
            addFact: 'Add Fact',
            addAnother: 'Add another',
            continueToNext: 'Continue to next',
            update: 'Update',
            addSignal: 'Add Signal',
            addTask: 'Add Task',
        },
        labels: {
            whatYouDid: "What you did/didn't do",
            whatWasYourAssumption: 'What was your assumption',
            signalsLearned: 'Signals Learned',
            debriefLeader: 'Debrief Leader',
            teamMember: 'Team Member',
            otherPerson: 'Other Person',
            additionalParticipant: 'Additional participant',
            progress: 'Progress',
            factsList: 'Facts List',
            viewAllFacts: 'View All Facts',
            closedAt: 'Closed at',
            startedAt: 'Started at',
            interactionDescription: 'Would you like to add a description of the interaction?',
        },
        placeholders: {
            enterFactPlaceholder: 'Enter a fact and press Ctrl+Enter / Alt + Enter...',
            enterSignalPlaceholder: 'Enter signal...',
            writeHerePlaceholder: 'Write here...',
            selectOption: 'Select Option',
            interactionDescription: 'Add description...',
        },
        messages: {
            noEmployeeSelected: 'No employee selected',
            participantNotFilledBot: "this participant hasn't filled out the bot yet",
            noMatchPoints: 'No match points found. Please complete the previous steps.',
            maxFactsReached: 'Maximum number of facts reached ({{count}}).',
            minFactsRequired: 'Please add at least {{count}} fact{{plural}}.',
            maxItemsSelected: 'Maximum number of items selected ({{count}})',
            entriesCount: '{{current}} / {{min}} entries',
            itemsSelectedSingular: 'one item selected',
            itemsSelectedPlural: '{{count}} items selected',
            selectUpToSingular: 'Select one item',
            selectUpToPlural: 'Select up to {{count}} items',
            selectedCount: '{{count}} / {{max}} selected',
            noFactsYet: 'No facts added yet. Add your first fact below.',
            viewersUpdatedSuccessfully: 'Viewers updated successfully',
            failedToUpdateViewers: 'Failed to update viewers',
        },
        goalsChoice: {
            noEffect: {
                title: 'Does not affect my goals',
            },
        },
        companyValuesChoice: {
            noEffect: {
                title: 'No, does not affect company values',
            },
        },
        exit: {
            exitWizard: 'Exit process',
            title: 'Exit process?',
            description: 'Are you sure you want to exit? Your progress will not be saved.',
            exit: 'Exit',
        },
        debug: {
            debugInfo: 'Debug Info',
        },
    },
};
